import type { SubmitAction, ActionResult } from '../types/actions';
import { isElementVisible, isElementEnabled, scrollIntoView } from '../utils/dom-utils';
import { waitForElement, waitForNavigation } from '../utils/wait-utils';

export async function executeSubmitAction(action: SubmitAction): Promise<ActionResult> {
  const startTime = Date.now();

  try {
    // The selector may point at the form itself OR at a submit button/input
    // inside it (the tool description allows both).
    let element = document.querySelector<HTMLElement>(action.selector);

    // Wait for element if specified
    if (!element && action.waitForElement) {
      element = await waitForElement<HTMLElement>(
        action.selector,
        action.waitForElement
      );
    }

    if (!element) {
      return {
        success: false,
        action,
        error: `Form not found: ${action.selector}`,
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: false
      };
    }

    // Resolve the target form: the element itself, the form a button/input
    // belongs to, or the nearest ancestor <form>.
    const form: HTMLFormElement | null =
      element instanceof HTMLFormElement
        ? element
        : ((element as HTMLButtonElement | HTMLInputElement).form ??
           element.closest('form'));

    if (!form) {
      return {
        success: false,
        action,
        error: 'Element is not a form and is not inside one',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Check if visible and enabled
    if (!isElementVisible(form)) {
      return {
        success: false,
        action,
        error: 'Form is not visible',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    if (!isElementEnabled(form)) {
      return {
        success: false,
        action,
        error: 'Form is disabled',
        timestamp: Date.now(),
        duration: Date.now() - startTime,
        elementFound: true
      };
    }

    // Scroll into view if needed
    if (action.scrollIntoView !== false) {
      scrollIntoView(form);
    }

    // Track if navigation might occur
    const currentUrl = window.location.href;

    // If the selector pointed at a submit control, pass it as the submitter so
    // the submit behaves exactly like a real user click (its name/value are
    // included). Otherwise submit the form with no explicit submitter.
    const submitter =
      element !== form &&
      ((element instanceof HTMLButtonElement && element.type === 'submit') ||
        (element instanceof HTMLInputElement &&
          (element.type === 'submit' || element.type === 'image')))
        ? (element as HTMLButtonElement | HTMLInputElement)
        : undefined;

    // requestSubmit() runs native validation and fires 'submit' handlers, which
    // form.submit() skips; fall back to submit() on very old engines.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.submit();
    }

    // Let the page settle after submit, then report success. A real navigation
    // resolves immediately on URL change; an in-place (SPA) submit renders its
    // result without navigating, so we fall through after a short settle delay
    // instead of waiting out the navigation timeout. Either way the submit was
    // already dispatched above, so a missing navigation is never a failure.
    if (action.waitForNavigation) {
      await Promise.race([
        waitForNavigation(currentUrl, 8000).catch(() => {}),
        new Promise<void>((resolve) => setTimeout(resolve, 1000)),
      ]);
    }

    return {
      success: true,
      action,
      timestamp: Date.now(),
      duration: Date.now() - startTime,
      elementFound: true
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: Date.now(),
      duration: Date.now() - startTime
    };
  }
}
